import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Send } from "lucide-react";

const DEPARTMENTS = ["Gas Station", "Beach Bar", "Admin", "Office"];

const blank = () => ({
  full_name: "",
  whatsapp_number: "",
  department: "",
  q_most_important: "",
  q_apply_this_week: "",
  q_do_differently: "",
  q_questions_thoughts: "",
});

/**
 * Admin shortcut dialog — facilitator manually enters a reflection on behalf of a participant.
 * Posts to the same /public/reflection endpoint so it triggers the same WhatsApp recap.
 */
const ManualReflectionDialog = ({ open, onOpenChange, workshop, defaultSession = 1, onSubmitted }) => {
  const [session, setSession] = useState(String(defaultSession));
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    setSession(String(defaultSession || 1));
  }, [defaultSession, open]);

  useEffect(() => {
    if (!open) setForm(blank());
  }, [open]);

  // Resolve per-session questions for display.
  useEffect(() => {
    if (!open || !workshop?.id) return;
    api
      .get(`/public/workshop-info/${workshop.id}?session=${session}`)
      .then((r) => setQuestions(r.data?.reflection_questions || []))
      .catch(() => setQuestions([
        "What is the most important thing I learned today?",
        "How will I apply this in my role this week?",
        "One thing I will do differently starting now",
        "Any questions or thoughts I still have",
      ]));
  }, [open, workshop?.id, session]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name || !form.whatsapp_number || !form.department) {
      toast.error("Name, WhatsApp number, and department are required");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/public/reflection", {
        workshop_id: workshop.id,
        session_number: Number(session),
        organisation: workshop.client_name || "",
        ...form,
      });
      const ws = res.data?.whatsapp_status;
      toast.success(
        ws === "sent"
          ? "Reflection saved · WhatsApp recap sent"
          : "Reflection saved"
      );
      if (onSubmitted) onSubmitted();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save reflection");
    } finally {
      setBusy(false);
    }
  };

  const keys = ["q_most_important", "q_apply_this_week", "q_do_differently", "q_questions_thoughts"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="manual-reflection-dialog">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-2">
            <UserPlus className="w-3 h-3" /> Facilitator entry
          </div>
          <DialogTitle className="font-display text-2xl">Add reflection on behalf of a participant</DialogTitle>
          <div className="text-xs text-[#6B5944]">
            For <strong>{workshop?.name}</strong>{workshop?.client_name ? ` · ${workshop.client_name}` : ""}.
            A WhatsApp recap will still be sent to the participant's number.
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Session *</Label>
              <Select value={session} onValueChange={setSession}>
                <SelectTrigger data-testid="manual-r-session-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>Session {n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs font-medium">Full Name *</Label>
              <Input data-testid="manual-r-name" value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="h-10" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">WhatsApp Number *</Label>
              <Input data-testid="manual-r-whatsapp" value={form.whatsapp_number} onChange={(e) => setF("whatsapp_number", e.target.value)} placeholder="868-123-4567" className="h-10" inputMode="tel" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Department *</Label>
              <Select value={form.department} onValueChange={(v) => setF("department", v)}>
                <SelectTrigger data-testid="manual-r-dept" className="h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(questions.length ? questions : [
            "Most important thing learned",
            "Will apply this week",
            "One thing to do differently",
            "Questions or thoughts",
          ]).map((label, i) => (
            <div key={keys[i]} className="space-y-1.5">
              <Label className="text-xs font-medium text-[#5D3A1F]">{i + 1}. {label}</Label>
              <Textarea
                data-testid={`manual-r-${keys[i]}`}
                rows={2}
                value={form[keys[i]]}
                onChange={(e) => setF(keys[i], e.target.value)}
                className="rounded-lg"
              />
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="manual-r-submit">
            <Send className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save & send WhatsApp recap"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualReflectionDialog;
